/**
 * Mobile Auth Handler for Capacitor
 * Handles authentication flow for mobile apps
 */

import { App } from '@capacitor/app';
import { isNativePlatform, isIOS, getPlatform } from './platform';
import { authStorage, deviceStorage } from './storage';
import {
  initializePushNotifications,
  requestPushPermission,
  removePushListeners,
} from './push-notifications';
import {
  registerDevice,
  deactivateDevice,
  sendHeartbeat,
  getDeviceInfo,
} from './device-registration';

export interface MobileAuthConfig {
  apiClient: {
    post: (url: string, data: any, config?: any) => Promise<any>;
    put: (url: string, data: any, config?: any) => Promise<any>;
    delete: (url: string, config?: any) => Promise<any>;
  };
  onTokenRefresh?: (newToken: string) => void;
  onSessionExpired?: () => void;
  onDeepLink?: (url: string) => void;
}

let config: MobileAuthConfig | null = null;
let heartbeatInterval: NodeJS.Timeout | null = null;

/**
 * Initialize mobile auth handling
 * Call this after the user logs in
 */
export async function initializeMobileAuth(authConfig: MobileAuthConfig): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  config = authConfig;

  // Initialize push notifications
  await initializePushNotifications({
    onRegistration: async (token, platform) => {
      console.log(`Push token received for ${platform}:`, token.substring(0, 20) + '...');

      // Store the token
      await deviceStorage.setPushToken(token);

      // Register device with backend
      const authToken = await authStorage.getAccessToken();
      if (authToken && config) {
        await registerDevice(config.apiClient, authToken, token);
      }
    },
    onRegistrationError: (error) => {
      console.error('Push registration error:', error);
    },
    onNotificationReceived: (notification) => {
      console.log('Notification received:', notification);
      // Handle foreground notification - this is handled by push-notifications.ts
    },
    onNotificationAction: (notification, actionId) => {
      console.log('Notification action:', actionId, notification);
      // Handle notification tap - navigate to appropriate screen
      handleNotificationTap(notification);
    },
  });

  // Request push permission
  const pushResult = await requestPushPermission();
  await deviceStorage.setPushEnabled(pushResult.granted);

  // Start heartbeat
  startHeartbeat();

  // Listen for app state changes
  await setupAppStateListeners();

  console.log('Mobile auth initialized');
}

/**
 * Handle notification tap - navigate to appropriate screen
 */
function handleNotificationTap(notification: { data?: Record<string, any> }): void {
  if (!notification.data) {
    return;
  }

  const { type, actionUrl } = notification.data;

  // If there's an actionUrl, use that for deep linking
  if (actionUrl) {
    config?.onDeepLink?.(actionUrl);
    return;
  }

  // Otherwise, handle based on notification type
  switch (type) {
    case 'incoming_call':
      config?.onDeepLink?.(`/dialer?incoming=${notification.data.callSid}`);
      break;
    case 'missed_call':
      config?.onDeepLink?.(`/leads/${notification.data.leadId}?tab=calls`);
      break;
    case 'sms_received':
      config?.onDeepLink?.(`/leads/${notification.data.leadId}?tab=messages`);
      break;
    case 'appointment_reminder':
      config?.onDeepLink?.(`/appointments/${notification.data.appointmentId}`);
      break;
    case 'reminder':
      config?.onDeepLink?.(`/reminders/${notification.data.reminderId}`);
      break;
    case 'lead_assigned':
      config?.onDeepLink?.(`/leads/${notification.data.leadId}`);
      break;
    case 'broadcast':
      // General broadcast - just go to notifications
      config?.onDeepLink?.('/notifications');
      break;
    default:
      // Default to notifications screen
      config?.onDeepLink?.('/notifications');
  }
}

/**
 * Setup app state change listeners
 */
async function setupAppStateListeners(): Promise<void> {
  // App state change (background/foreground)
  await App.addListener('appStateChange', async ({ isActive }) => {
    console.log('App state changed, isActive:', isActive);

    if (isActive) {
      // App came to foreground
      await onAppForeground();
    } else {
      // App went to background
      await onAppBackground();
    }
  });

  // App URL open (deep linking)
  await App.addListener('appUrlOpen', (event) => {
    console.log('App URL open:', event.url);
    config?.onDeepLink?.(event.url);
  });
}

/**
 * Called when app comes to foreground
 */
async function onAppForeground(): Promise<void> {
  // Send heartbeat
  const authToken = await authStorage.getAccessToken();
  if (authToken && config) {
    await sendHeartbeat(config.apiClient, authToken);
  }

  // Restart heartbeat interval
  startHeartbeat();
}

/**
 * Called when app goes to background
 */
async function onAppBackground(): Promise<void> {
  // Stop heartbeat interval to save battery
  stopHeartbeat();
}

/**
 * Start heartbeat interval
 */
function startHeartbeat(): void {
  if (heartbeatInterval) {
    return;
  }

  // Send heartbeat every 5 minutes
  heartbeatInterval = setInterval(async () => {
    const authToken = await authStorage.getAccessToken();
    if (authToken && config) {
      await sendHeartbeat(config.apiClient, authToken);
    }
  }, 5 * 60 * 1000);
}

/**
 * Stop heartbeat interval
 */
function stopHeartbeat(): void {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

/**
 * Handle user login
 * Call this after successful authentication
 */
export async function onMobileLogin(
  userId: string,
  accessToken: string,
  refreshToken?: string,
  userData?: any,
): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  // Store auth data
  await authStorage.setTokens(accessToken, refreshToken);
  await authStorage.setUserData(userId, userData);

  // Register device with push token
  const pushToken = await deviceStorage.getPushToken();
  if (config && pushToken) {
    await registerDevice(config.apiClient, accessToken, pushToken);
  }
}

/**
 * Handle user logout
 * Call this when user logs out
 */
export async function onMobileLogout(): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  // Stop heartbeat
  stopHeartbeat();

  // Deactivate device
  const authToken = await authStorage.getAccessToken();
  if (authToken && config) {
    await deactivateDevice(config.apiClient, authToken);
  }

  // Clear auth storage
  await authStorage.clearTokens();

  // Remove push listeners
  await removePushListeners();

  console.log('Mobile logout completed');
}

/**
 * Handle token refresh
 */
export async function onTokenRefresh(newAccessToken: string, newRefreshToken?: string): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  await authStorage.setTokens(newAccessToken, newRefreshToken);
  config?.onTokenRefresh?.(newAccessToken);
}

/**
 * Check if user is logged in on mobile
 */
export async function isMobileLoggedIn(): Promise<boolean> {
  if (!isNativePlatform()) {
    return false;
  }

  const token = await authStorage.getAccessToken();
  const userId = await authStorage.getUserId();
  return !!(token && userId);
}

/**
 * Get stored auth token for API requests
 */
export async function getMobileAuthToken(): Promise<string | null> {
  if (!isNativePlatform()) {
    return null;
  }

  return authStorage.getAccessToken();
}

/**
 * Cleanup mobile auth (call on app close)
 */
export async function cleanupMobileAuth(): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  stopHeartbeat();
  await App.removeAllListeners();
  config = null;
}

/**
 * Get mobile request headers
 */
export async function getMobileRequestHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};

  const authToken = await authStorage.getAccessToken();
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const deviceInfo = await getDeviceInfo();
  if (deviceInfo) {
    headers['X-App-Platform'] = deviceInfo.platform;
    headers['X-App-Version'] = deviceInfo.appVersion || '';
    headers['X-Device-Id'] = deviceInfo.deviceId;
    headers['X-OS-Version'] = deviceInfo.osVersion || '';
    headers['X-Client-Info'] = 'insuracentral-mobile';
  }

  return headers;
}
